<script>
import { mapGetters } from 'vuex';
import Banner from 'dashboard/components/ui/Banner.vue';
import {
  useStatusPage,
  STATUS_PAGE_URL,
} from 'dashboard/composables/useStatusPage';
import { useAnnouncements } from 'dashboard/composables/useAnnouncements';

export const DISMISS_STORAGE_KEY = 'incident_banner_dismissed';
export const DISMISS_DURATION_MS = 60 * 60 * 1000;
export const INCIDENT_PHASES = ['investigating', 'identified', 'monitoring'];
export const RESOLVED_SEVERITY = 'resolved';
const INDICATOR_LEVELS = ['none', 'minor', 'major', 'critical'];

const indicatorLevel = indicator => {
  const level = INDICATOR_LEVELS.indexOf(indicator);
  return level === -1 ? INDICATOR_LEVELS.length : level;
};

const isSubset = (items, of) =>
  Array.isArray(of) && items.every(item => of.includes(item));

const readDismissal = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};

const writeDismissal = value => {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

export default {
  name: 'IncidentBanner',
  components: { Banner },
  setup() {
    const { indicator, description, incidents, startPolling, stopPolling } =
      useStatusPage();
    const {
      announcements,
      ensureAccount,
      startListening: startAnnouncementsListening,
      stopListening: stopAnnouncementsListening,
    } = useAnnouncements();
    return {
      indicator,
      statusDescription: description,
      incidents,
      announcements,
      ensureAccount,
      startPolling,
      stopPolling,
      startAnnouncementsListening,
      stopAnnouncementsListening,
    };
  },
  data() {
    return {
      dismissal: readDismissal(),
      statusPageUrl: STATUS_PAGE_URL,
    };
  },
  computed: {
    ...mapGetters({ currentAccountId: 'getCurrentAccountId' }),
    incidentAnnouncements() {
      const incidents = this.announcements.filter(a => a.kind === 'incident');
      return [
        ...incidents.filter(a => a.severity !== RESOLVED_SEVERITY),
        ...incidents.filter(a => a.severity === RESOLVED_SEVERITY),
      ];
    },
    isResolved() {
      return (
        !this.hasStatusPageIncident &&
        this.incidentAnnouncements[0]?.severity === RESOLVED_SEVERITY
      );
    },
    hasStatusPageIncident() {
      return this.indicator !== 'none';
    },
    hasIncident() {
      return (
        this.hasStatusPageIncident || this.incidentAnnouncements.length > 0
      );
    },
    signature() {
      return {
        indicator: this.indicator,
        incidents: this.incidents.map(i => `${i.id}:${i.status || ''}`).sort(),
        announcements: this.incidentAnnouncements.map(a => a.id).sort(),
        resolved: this.isResolved,
      };
    },
    hasNothingNewSinceDismissal() {
      const dismissed = this.dismissal?.signature;
      if (!dismissed || typeof dismissed !== 'object') return false;
      const current = this.signature;
      return (
        (!current.resolved || dismissed.resolved === true) &&
        indicatorLevel(current.indicator) <=
          indicatorLevel(dismissed.indicator) &&
        isSubset(current.incidents, dismissed.incidents) &&
        isSubset(current.announcements, dismissed.announcements)
      );
    },
    isDismissed() {
      const stored = this.dismissal;
      if (!this.hasNothingNewSinceDismissal) return false;
      if (this.isResolved) return true;
      return Date.now() - stored.dismissedAt < DISMISS_DURATION_MS;
    },
    shouldShow() {
      return this.hasIncident && !this.isDismissed;
    },
    colorScheme() {
      if (this.isResolved) return 'success';
      const severity = this.hasStatusPageIncident
        ? this.indicator
        : this.incidentAnnouncements[0]?.severity;
      return severity === 'minor' ? 'warning' : 'alert';
    },
    bannerLink() {
      if (this.hasStatusPageIncident) return this.statusPageUrl;
      return this.incidentAnnouncements[0]?.link || this.statusPageUrl;
    },
    bannerLinkText() {
      return this.bannerLink === this.statusPageUrl
        ? this.$t('STATUS_PAGE.BANNER_LINK')
        : this.$t('STATUS_PAGE.ANNOUNCEMENT_LINK');
    },
    incidentPhase() {
      const status = this.incidents[0]?.status;
      return INCIDENT_PHASES.includes(status) ? status : null;
    },
    bannerMessage() {
      const headline =
        this.incidents[0]?.name ||
        this.incidentAnnouncements[0]?.title ||
        this.statusDescription;
      if (this.isResolved) {
        return this.$t('STATUS_PAGE.BANNER_RESOLVED', { headline });
      }
      if (!this.incidentPhase) {
        return this.$t('STATUS_PAGE.BANNER_MESSAGE', { headline });
      }
      return this.$t('STATUS_PAGE.BANNER_MESSAGE_WITH_PHASE', {
        headline,
        phase: this.$t(`STATUS_PAGE.PHASE.${this.incidentPhase}`),
      });
    },
  },
  watch: {
    currentAccountId: {
      immediate: true,
      handler(accountId) {
        this.ensureAccount(accountId);
      },
    },
  },
  mounted() {
    this.startPolling();
    this.startAnnouncementsListening();
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopAnnouncementsListening();
  },
  methods: {
    onDismiss() {
      this.dismissal = {
        signature: this.signature,
        dismissedAt: Date.now(),
      };
      writeDismissal(this.dismissal);
    },
  },
};
</script>

<template>
  <Banner
    v-if="shouldShow"
    :banner-message="bannerMessage"
    :href-link="bannerLink"
    :href-link-text="bannerLinkText"
    :color-scheme="colorScheme"
    :close-button-label="$t('STATUS_PAGE.CLOSE')"
    has-close-button
    @close="onDismiss"
  />
</template>
